import { isFuture, isPast } from 'date-fns';
import {
  type Event,
  type EventContentObjectDetail,
  type EventContentObjectList,
  type EventStatus,
  isEventInProgress,
  isProgramEventLive,
} from 'models';
import type { User } from 'features/auth';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { isAllowedChangeRequestStatus } from 'features/events/utils';
import { getLastChangeRequestStatus } from 'models/change-request';
import { isProgramEventInProgress } from 'models/program-event';
import { isCommunitiesAdmin, isOrganizationsAdmin, isSuperAdmin } from 'permissions/utils';
import { canManageContentObject, canReportContentObject } from '../content-object';

export const canEditTelehealthEvent = ({ event, viewer: { nodeUserData } }: { event: Event; viewer: User }) => {
  const { endTime, participants, startTime } = event;
  const medicalGroup = 'medicalGroup' in event ? event.medicalGroup : undefined;
  if (isPast(endTime) || isProgramEventLive({ endTime, startTime })) {
    return false;
  }

  const isEventModerator =
    participants.find((participant) => participant.ref === nodeUserData._id)?.role === 'moderator';

  const hasRoleInMedicalGroup = (role: string) =>
    Boolean(
      nodeUserData.memberships?.some(
        (membership) =>
          membership.type === 'group.medicalGroup' && membership.role === role && membership.ref === medicalGroup,
      ),
    );

  const isEngagementSupervisor = hasRoleInMedicalGroup('navigatorSupervisor');
  const isEngagementSpecialist = hasRoleInMedicalGroup('navigator');
  const isCallCenterCes = hasRoleInMedicalGroup('callCenterCes');

  return [isEventModerator, isEngagementSupervisor, isEngagementSpecialist, isCallCenterCes].some(Boolean);
};

export const canCreateWebinarEvent = ({ viewer }: { viewer: User }) =>
  Boolean(viewer.railsUserData.permissions.createWebinarEvent);

export const canCreateConferenceEvent = ({ viewer }: { viewer: User }) =>
  Boolean(viewer.railsUserData.permissions.createConferenceEvent);

export const canRequestTelehealthEvent = ({ viewer: { nodeUserData } }: { viewer: User }) =>
  Boolean(
    nodeUserData.memberships?.some(
      (membership) => membership.type === 'group.medicalGroup' && membership.role === 'member',
    ),
  );

export const canCreateEvent = ({ viewer }: { viewer: User }) =>
  canCreateConferenceEvent({ viewer }) || canCreateWebinarEvent({ viewer });

export const canEditEvent = ({
  contentObject,
  view,
  viewer,
}: {
  contentObject: EventContentObjectDetail | EventContentObjectList;
  view?: 'detail' | 'feed' | 'program';
  viewer: User;
}) => {
  if (contentObject.contentableType === 'Program' && view !== 'program') {
    return false;
  }

  const { changeRequests, endTime, startTime } = contentObject.event;
  const status = getLastChangeRequestStatus(changeRequests);
  const isAllowedStatus = isAllowedChangeRequestStatus({ status });

  const isLive = isProgramEventInProgress({
    endTime,
    startTime,
  });

  const isEventPast = isPast(startTime);

  if (isEventPast || isLive || !isAllowedStatus) {
    return false;
  }

  return canManageContentObject({ contentObject, viewer });
};

export const canEditSocialEvent = ({ event, viewer }: { event: Event; viewer: User }) => {
  const { author, endTime, startTime } = event;

  const isLive = isEventInProgress({
    endTime,
    startTime,
  });

  const isEventPast = isPast(startTime);

  if (isEventPast || isLive) {
    return false;
  }

  return isEventAuthor({ authorId: author, viewer });
};

const isEventAuthor = ({ authorId, viewer: { nodeUserData } }: { authorId?: string; viewer: User }) =>
  nodeUserData._id === authorId;

export const canReportEvent = ({ contentObject, viewer }: { contentObject: EventContentObjectDetail; viewer: User }) =>
  canReportContentObject({ contentObject, viewer });

export const canReportSocialEvent = ({ event, viewer }: { event: Event; viewer: User }) =>
  !isEventAuthor({ authorId: event.author, viewer });

export const canCancelSocialEvent = ({ event, viewer }: { event: Event; viewer: User }) =>
  canEditSocialEvent({ event, viewer });

export const canDuplicateSocialEvent = ({ event, viewer }: { event: Event; viewer: User }) =>
  isEventAuthor({ authorId: event.author, viewer });

export const canDeclineEvent = ({ authorId, viewer: { nodeUserData } }: { authorId?: string; viewer: User }) =>
  nodeUserData._id !== authorId;

export const canDuplicateEvent = ({
  contentObject,
  viewer,
}: {
  contentObject: EventContentObjectDetail;
  viewer: User;
}) => {
  const { changeRequests } = contentObject.event;
  const status = getLastChangeRequestStatus(changeRequests);
  const isAllowedStatus = isAllowedChangeRequestStatus({
    allowedStatuses: ['approved', 'denied', undefined],
    status,
  });

  if (contentObject.contentableType === 'Program') {
    return false;
  }

  if (!isAllowedStatus) {
    return false;
  }

  return canManageContentObject({ contentObject, viewer });
};

export const canDeleteEvent = ({
  contentObject,
  view,
  viewer,
}: {
  contentObject: EventContentObjectDetail | EventContentObjectList;
  view?: 'detail' | 'feed' | 'program';
  viewer: User;
}) => {
  if (contentObject.contentableType === 'Program' && view !== 'program') {
    return false;
  }

  const { changeRequests, endTime, startTime } = contentObject.event;
  const status = getLastChangeRequestStatus(changeRequests);
  const isAllowedStatus = isAllowedChangeRequestStatus({
    allowedStatuses: ['approved', 'denied', undefined],
    status,
  });

  const isLive = isProgramEventInProgress({
    endTime,
    startTime,
  });

  const isEventPast = isPast(startTime);

  if (isEventPast || isLive || !isAllowedStatus) {
    return false;
  }

  return canManageContentObject({ contentObject, viewer });
};

export const canViewEventApprovalStatus = ({
  eventContentObject,
  viewer,
}: {
  eventContentObject: EventContentObjectDetail;
  viewer: User;
}) => canManageContentObject({ contentObject: eventContentObject, viewer });

export const canManageEventUsers = ({
  contentObject,
  viewer,
}: {
  contentObject: EventContentObjectDetail;
  viewer: User;
}) => {
  const { changeRequests, endTime, startTime } = contentObject.event;
  const status = getLastChangeRequestStatus(changeRequests);
  const isAllowedStatus = isAllowedChangeRequestStatus({ status });

  const isLive = isProgramEventInProgress({
    endTime,
    startTime,
  });

  const isEventPast = isPast(startTime);

  if (!isAllowedStatus || (isEventPast && !isLive)) {
    return false;
  }

  return canManageContentObject({ contentObject, viewer });
};

export const canChangeUserRoleInEvent = ({
  contentObject,
  viewer,
}: {
  contentObject: EventContentObjectDetail;
  viewer: User;
}) => canManageEventUsers({ contentObject, viewer });

export const canInviteRailsUserToEvent = ({
  contentObject,
  viewer,
}: {
  contentObject: EventContentObjectDetail;
  viewer: User;
}) => {
  if (contentObject.contentableType === 'Program') {
    return false;
  }

  return canManageEventUsers({ contentObject, viewer });
};

export const canDeleteRailsUserFromEvent = ({
  contentObject,
  viewer,
}: {
  contentObject: EventContentObjectDetail;
  viewer: User;
}) => canInviteRailsUserToEvent({ contentObject, viewer });

export const hasMedicalGroup = ({ viewer: { nodeUserData } }: { viewer: User }) =>
  Boolean(nodeUserData.memberships?.some((membership) => membership.type === 'group.medicalGroup'));

export const canViewTelehealth = ({ viewer }: { viewer: User }) => hasMedicalGroup({ viewer });

export const canInviteCommunities = ({ viewer }: { viewer: User }) => isEventsAdmin({ viewer });

export const isEventsAdmin = ({ viewer: { railsUserData } }: { viewer: User }) =>
  isCommunitiesAdmin({ viewer: railsUserData }) ||
  isOrganizationsAdmin({ viewer: railsUserData }) ||
  isSuperAdmin({ viewer: railsUserData });

export const canManageEventChannels = ({ viewer: { railsUserData } }: { viewer: User }) =>
  isOrganizationsAdmin({ viewer: railsUserData }) || isSuperAdmin({ viewer: railsUserData });

export const canInviteUserToEvent = ({
  event: { author, endTime, status },
  viewer: {
    nodeUserData: { _id },
  },
}: {
  event: Event;
  viewer: User;
}) => {
  if (isCanceledEvent({ status }) || isPast(endTime)) {
    return false;
  }

  return author === _id;
};

const isCanceledEvent = ({ status }: { status: EventStatus }) => status === 'canceled';

export const canDeleteUserFromEvent = ({ event, viewer }: { event: Event; viewer: User }) =>
  !isCanceledEvent({ status: event.status }) &&
  isFuture(event.startTime) &&
  isEventAuthor({ authorId: event.author, viewer });

export const canEditUserRoleInEvent = ({ event, viewer }: { event: Event; viewer: User }) =>
  canDeleteUserFromEvent({ event, viewer });
